import React from 'react';
import { ArrowRight, Calculator, CheckCircle2, MapPin, Layers, Radio, Sparkles, Droplets, ShieldCheck } from 'lucide-react';

const Hero = ({ onOpenContact }) => {
  return (
    <section
      id="hero"
      style={{
        position: 'relative',
        minHeight: '100vh',
        paddingTop: '9.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'radial-gradient(circle at 50% 10%, #ffffff 0%, #f8fafc 100%)',
      }}
    >
      {/* Background Decorative Soft Glows */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '15%',
          width: '550px',
          height: '550px',
          background: 'radial-gradient(circle, rgba(230, 57, 70, 0.08) 0%, rgba(255, 255, 255, 0) 70%)',
          filter: 'blur(50px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(0, 102, 255, 0.08) 0%, rgba(255, 255, 255, 0) 70%)',
          filter: 'blur(60px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Grid Pattern Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
          opacity: 0.6,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="grid-2" style={{ gap: '3.5rem', alignItems: 'center' }}>
          {/* Left Column: Hero Copy */}
          <div>
            {/* Top Pill Badge */}
            <div
              className="badge badge-gold"
              style={{ marginBottom: '1.25rem', boxShadow: '0 4px 15px rgba(217, 119, 6, 0.15)' }}
            >
              <Sparkles size={14} /> Accurate Survey, Better Tomorrow
            </div>

            {/* Main Hero Headline */}
            <h1
              style={{
                fontSize: 'clamp(2.3rem, 4.5vw, 3.75rem)',
                lineHeight: 1.1,
                marginBottom: '1.25rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: 800,
                color: '#0f172a',
              }}
            >
              Accurate Land Survey & <br />
              <span style={{ background: 'linear-gradient(135deg, #0f172a 0%, #e63946 50%, #0066ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Digital Consulting
              </span>
            </h1>

            {/* Subtitle & Tagline */}
            <p
              style={{
                fontSize: '1.15rem',
                color: '#e63946',
                fontWeight: '700',
                fontStyle: 'italic',
                marginBottom: '0.75rem',
              }}
            >
              "Let Your Dreams be our Project"
            </p>

            <p
              style={{
                fontSize: '1.05rem',
                color: '#475569',
                lineHeight: 1.65,
                marginBottom: '2rem',
              }}
            >
              High-precision DGPS Land Surveys, Boundary Mapping, Water Resources Development (W.R.D Work), Highways & Road Alignment, Volumetric Calculations, and 2D CAD Consulting across Tamil Nadu & Pan-India.
            </p>

            {/* CTAs */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                marginBottom: '2.5rem',
              }}
            >
              <button onClick={() => onOpenContact()} className="btn-primary btn-glow">
                Request Instant Quote <ArrowRight size={18} />
              </button>
              <a href="#calculator" className="btn-secondary">
                <Calculator size={18} /> Interactive Estimator
              </a>
            </div>

            {/* Trust Highlights */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.85rem',
                color: '#475569',
                fontSize: '0.875rem',
                fontWeight: '600',
                borderTop: '1px solid #e2e8f0',
                paddingTop: '1.5rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle2 size={16} color="#e63946" /> High-Accuracy DGPS Survey
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle2 size={16} color="#0284c7" /> Boundary Land Details
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Droplets size={16} color="#0066ff" /> Water Resources (W.R.D Work)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle2 size={16} color="#d97706" /> Digital CAD & Consulting
              </div>
            </div>
          </div>

          {/* Right Column: Authentic Real Field Work Photo Frame */}
          <div style={{ position: 'relative' }}>
            <div
              className="glass-card"
              style={{
                padding: '1.5rem',
                borderRadius: '24px',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.15)',
                position: 'relative',
                textAlign: 'center',
              }}
            >
              {/* Circular Round Photo */}
              <div
                style={{
                  borderRadius: '50%',
                  overflow: 'hidden',
                  width: '310px',
                  height: '310px',
                  margin: '0 auto 1.25rem auto',
                  boxShadow: '0 12px 30px rgba(15, 23, 42, 0.15)',
                  border: '4px solid #ffffff',
                  background: '#f8fafc',
                }}
              >
                <img
                  src="/b_digital_badge.jpg"
                  alt="B Digital Land Survey & Consulting Badge"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    transform: 'scale(1.02)',
                  }}
                />
              </div>

              {/* Text Content Preserved */}
              <div style={{ padding: '0 0.5rem', textAlign: 'center' }}>
                <div className="badge badge-gold" style={{ marginBottom: '0.75rem', display: 'inline-flex' }}>
                  <Sparkles size={13} /> Authentic Site Survey Operations
                </div>
                <h3 style={{ fontSize: '1.35rem', color: '#0f172a', fontWeight: '800', marginBottom: '0.35rem' }}>
                  DGPS High-Accuracy Land Survey & Consulting
                </h3>
                <p style={{ color: '#e63946', fontSize: '0.925rem', fontWeight: '700', fontStyle: 'italic' }}>
                  "(Let Your Dreams be our Project)"
                </p>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.35rem' }}>
                  Theni & Tamil Nadu Regional Projects
                </p>
              </div>
            </div>

            {/* Floating Badge Card */}
            <div
              style={{
                position: 'absolute',
                top: '-1rem',
                right: '-1rem',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                padding: '0.85rem 1.2rem',
                borderRadius: '16px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#fff5f5',
                  color: '#e63946',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Radio size={22} />
              </div>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0f172a' }}>RTK DGPS System</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Boundary Survey Precision</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Performance Indicators */}
        <div
          className="grid-4"
          style={{
            marginTop: '4rem',
          }}
        >
          <div className="glass-card" style={{ padding: '1.75rem 1.5rem', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                padding: '0.75rem',
                borderRadius: '12px',
                background: '#fff5f5',
                color: '#e63946',
                marginBottom: '1rem',
              }}
            >
              <Radio size={28} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: '800', color: '#0f172a', fontFamily: 'var(--font-heading)' }}>
              DGPS
            </div>
            <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              High Accuracy Dual-Frequency Satellite Survey
            </div>
          </div>

          <div className="glass-card" style={{ padding: '1.75rem 1.5rem', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                padding: '0.75rem',
                borderRadius: '12px',
                background: '#eff6ff',
                color: '#0066ff',
                marginBottom: '1rem',
              }}
            >
              <Droplets size={28} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: '800', color: '#0f172a', fontFamily: 'var(--font-heading)' }}>
              W.R.D. Work
            </div>
            <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Water Resources Development Surveying
            </div>
          </div>

          <div className="glass-card" style={{ padding: '1.75rem 1.5rem', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                padding: '0.75rem',
                borderRadius: '12px',
                background: '#f0f9ff',
                color: '#0284c7',
                marginBottom: '1rem',
              }}
            >
              <Layers size={28} />
            </div>
            <div style={{ fontSize: '1.65rem', fontWeight: '800', color: '#0f172a', fontFamily: 'var(--font-heading)' }}>
              Boundary Survey
            </div>
            <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Precise Land Boundary Demarcation
            </div>
          </div>

          <div className="glass-card" style={{ padding: '1.75rem 1.5rem', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                padding: '0.75rem',
                borderRadius: '12px',
                background: '#fffbe6',
                color: '#d97706',
                marginBottom: '1rem',
              }}
            >
              <MapPin size={28} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: '800', color: '#0f172a', fontFamily: 'var(--font-heading)' }}>
              Theni, Tamil Nadu
            </div>
            <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Main Office & Field Operations
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
